<template>
  <WizardOverlayContainer
    :activated="wizardFocus"
    @unfocus="unfocus"
    theme="gray">
    <div class="wizard-header" v-if="wizardFocus">
      <a
        v-if="disability || subjects.length > 0 || impactAreas.length > 0"
        @click="reset"
        @keypress.enter="reset"
        tabindex="0"
        role="button"
        class="btn btn-link"><i class='fa fa-rotate-left' /> Start Over</a>
      <span
        @click="resetAndUnfocus"
        v-if="wizardFocus"
        @keypress.enter="resetAndUnfocus"
        tabindex="0"
        class="close-button"
        role="button"
        aria-label="close impact of disability wizard pop-up"
      ><i class="fa fa-close fa-2x"/></span>
    </div>
    <p class="prompt disability-prompt">My student is identified with having the following disability:
      <A11yDropdown
        :dropdown-props="{
          ref:'disabilities'
        }"
        :button-props="{
          id:'disabilitiesButton',
          class:'btn btn-lg btn-default dropdown-toggle' + ( disability ? '' : ' text-muted'),
          'aria-haspopup':'listbox',
          'aria-label':'My student is identified with having the following disability',
          role:'combobox',
          'aria-controls':'disabilitiesDropdown',
          tabindex:0
        }"
        :dom-attributes="{
          'aria-labelledby':'disabilitiesButton',
          id:'disabilitiesDropdown',
          role:'listbox'
        }"
        @openedDropdown="wizardFocus = true"
      >
        <template slot="button-contents">{{ disability ? getDisabilityLabel(disability) : "disability" }} <span class="caret"/></template>
        <template slot="dropdown-items">
          <li v-if="loadingOptions" class="dropdown-menu-loading"><i class="fa fa-circle-o-notch fa-spin"/></li>
          <li
            class="dropdown-item"
            v-for="disabilityOption in disabilityOptions"
            :key="disabilityOption.value"
            :class="{
              'cursor-not-allowed': disabilityOption.locked,
              'locks': !isPremium
            }"
          >
            <template v-if="disabilityOption.locked">
              <LockedOption :label="disabilityOption.label" />
            </template>
            <a
              v-else
              role="button"
              @click="updateDisability(disabilityOption)">{{ disabilityOption.label }}</a>
          </li>
        </template>
      </A11yDropdown>
    </p>

    <transition name="fade">
      <div v-if="disability">
        <p class="prompt" v-if="subjectOptions">Their disability impacts them in the following subjects:</p>
        <div class="row">
          <div class="col-md-8">
            <RadioRow
              :columns="3"
              label="right"
              :option-set="subjectOptions"
              :multi-select="true"
              :default-options="[]"
              v-model="selectedSubjects"
              @selectedOptions="(options) => this.$emit('update:subjects', options)"
            />
          </div>
        </div>
        <p class="prompt" v-if="!loadingOptions && impactAreaOptions.length > 1">Their disability impacts them in the following areas:</p>
        <div class="row" v-if="!loadingOptions && impactAreaOptions.length > 1">
          <div class="col-md-10">
            <ChipsInput
              :items="impactAreaOptions"
              v-model="selectedImpactAreas"
              :selected-options="impactAreas"
              :show-icons="true"
              @input="$emit('update:impactAreas', selectedImpactAreas)"
            />
          </div>
        </div>
      </div>
    </transition>
    <transition name="fade">
      <div v-if="disability && subjects.length > 0 && ((impactAreaOptions.length > 1 && impactAreas.length > 0) || impactAreaOptions.length <= 1)">
        <p class="text-center">
          <button
            class="btn btn-lg btn-purple"
            @click="getIODResults()"
          >Compose impact of disability statement</button>
        </p>
      </div>
    </transition>
  </WizardOverlayContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import WizardOverlayContainer from '@/components/WizardOverlayContainer'
import RedundantRouteMixin from '@/components/RedundantRouteMixin.js'
import A11yDropdown from '@/components/A11yDropdown'
import RadioRow from '@/components/presentlevels/RadioRow'
import ChipsInput from '@/components/ChipsInput'
import LockedOption from '@/components/LockedOption'

export default {
  name: 'ImpactOfDisabilityWizard',
  components: {
    WizardOverlayContainer,
    A11yDropdown,
    RadioRow,
    ChipsInput,
    LockedOption
  },
  mixins: [RedundantRouteMixin],
  props: {
    disability: {
      type: String,
      default: ''
    },
    disabilityOptions: {
      type: Array,
      required: true
    },
    loadingOptions: {
      type: Boolean,
    },
    subjects: {
      type: Array,
      default: () => []
    },
    subjectOptions: {
      type: Array,
      required: true
    },
    impactAreas: {
      type: Array,
      default: () => []
    },
    impactAreaOptions: {
      type: Array,
      required: true
    },
  },
  data() {
    return {
      wizardFocus: false,
      selectedImpactAreas: this.impactAreas,
      selectedSubjects: this.subjects,
    }
  },
  computed: {
    ...mapGetters('user',['isPremium']),
  },
  methods: {
    resetAndUnfocus() {
      this.reset();
      this.unfocus();
    },
    unfocus() {
      this.wizardFocus = false
    },
    reset() {
      this.$emit('update:disability', '')
      this.$emit('update:impactAreas', [])
      this.$emit('update:subjects', [])
    },
    getIODResults() {
      let newQuery = Object.assign({}, this.query)
      newQuery.subjects = this.subjects
      newQuery.disability = this.disability
      newQuery.impactAreas = this.impactAreas.map(ia => ia.area_uuid)
      this.$sendEvent('Clicked Compose Impact of Disability Statement')
      this.rerouteIfRouteChanged(this.$route, {
        query: newQuery,
        name: 'ImpactOfDisability'
      })
    },
    updateDisability(disabilityOption) {
      if (this.disability != disabilityOption.value) {
        this.$emit('update:disability', disabilityOption.value)
        this.$emit('update:impactAreas', [])
      }
    },
    getDisabilityLabel(disability) {
      if (disability) {
        return this.disabilityOptions.filter(elem => elem.value === disability)[0].label
      } else {
        return ''
      }
    }
  }
}
</script>

<style scoped lang="less">
@import '~@/variables.less';
.prompt {
  font-size: 24px;
  font-style: italic;
  line-height: 40px;
  font-weight: normal;
  color: @gray-dark;
  margin-bottom: 0;
  label {
    font-weight: 400;
  }
  &.disability-prompt {
    margin-bottom: 1rem;
    line-height: 60px;
  }
}
.wizard-header {
  display: flex;
  padding-bottom: 20px;
}
.radio-row {
  font-style: normal;
  padding-bottom: 1rem;
  /deep/ * {
    color: @gray-dark;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  }
}
.dropdown {
  font-style: normal;
}
.close-button {
  position: absolute;
  top: 16px;
  right: 20px;
}
.text-muted {
  color: @gray-light !important;
}
.btn-lg {
  font-size: 24px;
  padding: 16px 24px;
  margin: 24px auto 12px auto;
}

.btn-link {
  color: #1f496e;
  padding-left: 0;
}
.btn-purple {
  background: @plPurple;
  &:hover, &:focus {
    background: darken(@plPurple, 10%)
  }
}
.caret {
  margin-left: 10px;
  width: 10px;
}
/deep/ .mdc-form-field > label {
  font-weight: normal;
  font-size: 1.2rem;
  cursor: pointer;
}
.locked-option {
  line-height: 1.5rem;
  padding: 3px 20px 3px 3px;
}
.locks a {
  padding-left: 24px;
}
</style>
